import React, { useState, useEffect } from "react";
import { NavLink, Outlet, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { APP_STORE_URL } from "@/components/marketing/AppStoreBadge";
import {
  LayoutDashboard, CalendarDays, Users, UserCog, Scissors, Settings, LogOut, Building2, Repeat,
  Calculator, FlaskConical, ListChecks, SlidersHorizontal, Menu, X, Package, Gift, BarChart3, FileText,
  CreditCard, MessageSquare, Clock, Plug, Stethoscope, Sparkles, Bot, Trophy, HeartPulse, HelpCircle, Receipt, ChevronRight, Megaphone, Upload, Smartphone, Wallet } from "lucide-react";

import { TrialBanner } from "@/components/booking/TrialBanner";
import { NotificationBell } from "@/components/booking/NotificationBell";
import { LowCreditBanner } from "@/components/booking/LowCreditBanner";
import { UserSwitchOverlay } from "@/components/booking/UserSwitch";

const PRIMARY_NAV = [
  { to: "/dashboard", label: "Intelligence", icon: LayoutDashboard, end: true, roles: ["admin"] },
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true, roles: ["staff"], perm: "view_dashboard", hideFor: ["admin"] },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, roles: ["admin", "staff", "reception"] },
  { to: "/sales", label: "Sales", icon: Receipt, roles: ["admin", "staff", "reception"] },
  { to: "/clients", label: "Clients", icon: Users, roles: ["admin", "reception", "staff"] },
  { to: "/waitlist", label: "Waitlist", icon: Clock, roles: ["admin", "reception"] },
  { to: "/messages", label: "Messages", icon: MessageSquare, roles: ["admin"] },
  { to: "/reports", label: "Reports", icon: BarChart3, roles: ["admin"], perm: "view_reports" },
  { to: "/finance", label: "Finance", icon: Wallet, roles: ["admin"], ownerOnly: true, financeOnly: true },
];

// Everything else lives under one "Setup" entry, grouped into columns (Timely-style)
const SETUP_GROUPS = [
  { label: "Your business", items: [
    { to: "/business-info", label: "Business details", icon: Building2, roles: ["admin"] },
    { to: "/staff", label: "Staff & roster", icon: UserCog, roles: ["admin"] },
    { to: "/services", label: "Services", icon: Scissors, roles: ["admin"], ownerOnly: true },
    { to: "/settings", label: "Settings", icon: Settings, roles: ["admin"] },
    { to: "/import", label: "Import data", icon: Upload, roles: ["admin"] },
    { to: "/intelligence/memory", label: "Business Memory", icon: Sparkles, roles: ["admin"] },
    { to: "/integrations", label: "Integrations", icon: Plug, roles: ["admin"] },
    { to: "/billing", label: "Billing", icon: CreditCard, roles: ["admin"] },
  ] },
  { label: "Clinical", items: [
    { to: "/forms", label: "Consult forms", icon: FileText, roles: ["admin", "reception"] },
    { to: "/chart-templates", label: "Treatment charts", icon: Stethoscope, roles: ["admin"] },
    { to: "/aftercare-templates", label: "Aftercare templates", icon: HeartPulse, roles: ["admin"] },
  ] },
  { label: "Stock & pricing", items: [
    { to: "/inventory", label: "Inventory", icon: Package, roles: ["admin"] },
    { to: "/packages", label: "Packages", icon: Gift, roles: ["admin"] },
    { to: "/price-list", label: "Price list", icon: ListChecks, roles: ["admin", "staff", "reception"] },
    { to: "/business-setup", label: "Defaults & tax", icon: SlidersHorizontal, roles: ["admin"] },
  ] },
  { label: "Grow", items: [
    { to: "/rewards", label: "Rewards & referrals", icon: Trophy, roles: ["admin"] },
    { to: "/meta-ads", label: "Meta ads", icon: Megaphone, roles: ["admin"] },
  ] },
  { label: "Cost tools", items: [
    { to: "/tools/calculator", label: "Laser calculator", icon: Calculator, roles: ["admin"] },
    { to: "/treatments", label: "Treatment builder", icon: FlaskConical, roles: ["admin"] },
  ] },
];
const SETUP_PATHS = SETUP_GROUPS.flatMap((g) => g.items.map((it) => it.to));

const roleKey = (r) => (r === "owner" || r === "admin" ? "admin" : r);

export const AppShell = () => {
  const { user, business, logout, can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isCalendar = location.pathname.startsWith("/calendar");
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showSwitch, setShowSwitch] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [locked, setLocked] = useState(() => sessionStorage.getItem("pc_locked") === "1");

  useEffect(() => { setSetupOpen(false); }, [location.pathname]);
  const lock = () => { sessionStorage.setItem("pc_locked", "1"); setLocked(true); };
  const unlock = () => { sessionStorage.removeItem("pc_locked"); setLocked(false); };

  const lockMins = business?.auto_lock_minutes ?? 10;
  useEffect(() => {
    if (!user) return;
    if (lockMins === 0) { unlock(); return; }
    let t;
    const reset = () => { clearTimeout(t); t = setTimeout(lock, lockMins * 60 * 1000); };
    const evs = ["mousemove", "keydown", "click", "touchstart", "scroll"];
    evs.forEach((e) => window.addEventListener(e, reset));
    if (sessionStorage.getItem("pc_locked") !== "1") reset();
    return () => { clearTimeout(t); evs.forEach((e) => window.removeEventListener(e, reset)); };
  }, [user, lockMins]);

  if (user === null) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  }
  if (user === false) return <Navigate to="/login" replace />;

  const rk = roleKey(user?.role);
  const allowed = (it) => (it.roles.includes(rk) || (it.perm && can(it.perm))) && (!it.ownerOnly || user?.role === "owner") && (!it.financeOnly || business?.finance_centre_enabled) && !(it.hideFor || []).includes(rk);
  const primary = PRIMARY_NAV.filter(allowed);
  const setupGroups = SETUP_GROUPS.map((g) => ({ ...g, items: g.items.filter(allowed) })).filter((g) => g.items.length);
  const inSetup = SETUP_PATHS.some((p) => location.pathname.startsWith(p));
  const navCls = (isActive, mini) => `flex items-center gap-2.5 py-2 rounded-lg text-sm font-medium mb-0.5 transition-colors w-full ${mini ? "px-0 justify-center" : "px-3"} ${isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`;

  const NavItems = ({ mobile = false, mini = false } = {}) => (
    <>
      {primary.map((it) => (
        <NavLink key={it.to} to={it.to} end={it.end} onClick={() => setOpen(false)} title={mini ? it.label : undefined}
          data-testid={`${mobile ? "mnav" : "nav"}-${it.label.replace(/\s+/g, "-").toLowerCase()}`}
          className={({ isActive }) => navCls(isActive, mini)}>
          <it.icon className="h-4 w-4 shrink-0" />{!mini && it.label}
        </NavLink>
      ))}
      {setupGroups.length > 0 && !mobile && (
        <button onClick={() => setSetupOpen((v) => !v)} title={mini ? "Setup" : undefined} data-testid="nav-setup"
          className={navCls(inSetup || setupOpen, mini)} aria-expanded={setupOpen}>
          <Settings className="h-4 w-4 shrink-0" />{!mini && <>Setup<ChevronRight className={`h-3.5 w-3.5 ml-auto transition-transform ${setupOpen ? "rotate-90" : ""}`} /></>}
        </button>
      )}
      {mobile && setupGroups.map((g) => (
        <div key={g.label} className="mt-4">
          <div className="px-3 mb-1.5 font-mono-label text-[10px] uppercase tracking-widest text-muted-foreground">{g.label}</div>
          {g.items.map((it) => (
            <NavLink key={it.to} to={it.to} onClick={() => setOpen(false)} data-testid={`mnav-${it.label.replace(/\s+/g, "-").toLowerCase()}`}
              className={({ isActive }) => navCls(isActive, false)}>
              <it.icon className="h-4 w-4 shrink-0" />{it.label}
            </NavLink>
          ))}
        </div>
      ))}
    </>
  );

  const SetupMenu = () => (
    <div className="fixed inset-0 z-[60]" onClick={() => setSetupOpen(false)} data-testid="setup-menu-backdrop">
      <div onClick={(e) => e.stopPropagation()} className={`absolute top-16 ${expanded ? "left-60" : "left-16"} bg-card border border-border rounded-r-2xl shadow-2xl p-6 grid gap-8 animate-in fade-in-0 slide-in-from-left-2 duration-150`}
        style={{ gridTemplateColumns: `repeat(${Math.min(setupGroups.length, 3)}, minmax(170px, 1fr))` }} data-testid="setup-menu">
        {setupGroups.map((g) => (
          <div key={g.label}>
            <div className="font-mono-label text-[10px] uppercase tracking-widest text-muted-foreground mb-2">{g.label}</div>
            {g.items.map((it) => (
              <NavLink key={it.to} to={it.to} onClick={() => setSetupOpen(false)} data-testid={`setup-${it.label.replace(/[\s&]+/g, "-").toLowerCase()}`}
                className={({ isActive }) => `flex items-center gap-2 py-1.5 text-sm rounded-md -mx-1 px-1 transition-colors ${isActive ? "text-primary font-medium" : "text-foreground/80 hover:text-primary"}`}>
                <it.icon className="h-3.5 w-3.5 shrink-0 opacity-70" />{it.label}
              </NavLink>
            ))}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar: slim icon rail that expands on hover (large screens only) */}
      <aside className="hidden xl:block w-16 shrink-0">
        <div
          onMouseEnter={() => setExpanded(true)}
          onMouseLeave={() => setExpanded(false)}
          className={`fixed left-0 top-0 h-screen flex flex-col border-r border-border bg-card overflow-hidden transition-[width] duration-200 ease-out ${expanded ? "w-60 shadow-2xl z-50" : "w-16 z-30"}`}
          data-testid="sidebar-rail"
        >
          <div className={`h-16 flex items-center border-b border-border shrink-0 ${expanded ? "gap-2.5 px-4" : "justify-center px-2"}`}>
            {!expanded ? (
              <img src="/azordi-icon.png" alt="Azordi" className="h-9 w-9 object-contain" data-testid="sidebar-brand-icon-mini" />
            ) : business?.logo_url ? (
              <>
                <img src={business.logo_url.startsWith("/") ? process.env.REACT_APP_BACKEND_URL + business.logo_url : business.logo_url} alt={business?.name || "logo"} className="h-10 w-10 rounded-xl object-cover border border-border shrink-0" data-testid="sidebar-logo" />
                <div className="leading-tight min-w-0">
                  <div className="font-heading font-extrabold tracking-tight truncate">{business?.name}</div>
                  <div className="font-mono-label text-[10px] uppercase tracking-widest text-muted-foreground">Booking Suite</div>
                </div>
              </>
            ) : (
              <img src="/azordi-logo.webp" alt="Azordi" className="h-7 w-auto object-contain" data-testid="sidebar-brand-icon" />
            )}
          </div>
          <nav className={`flex-1 overflow-y-auto overflow-x-hidden ${expanded ? "p-3" : "p-2"}`}>{NavItems({ mini: !expanded })}</nav>
          <div className={`border-t border-border shrink-0 ${expanded ? "p-3" : "p-2"}`}>
            {expanded && (
              <div className="px-3 py-2 text-sm">
                <div className="font-medium truncate">{user.name}</div>
                <div className="text-xs text-muted-foreground truncate capitalize">{user.role}</div>
              </div>
            )}
            <a href={APP_STORE_URL} target="_blank" rel="noreferrer" data-testid="app-download-link" title={!expanded ? "Get the iPhone app" : undefined}
              className={`flex items-center gap-2 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-primary hover:bg-muted w-full transition-colors ${expanded ? "px-3" : "justify-center px-0"}`}>
              <Smartphone className="h-4 w-4 shrink-0" /> {expanded && "Get the iPhone app"}
            </a>
            <a href="/help" target="_blank" rel="noreferrer" data-testid="help-link" title={!expanded ? "Help Centre" : undefined}
              className={`flex items-center gap-2 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-primary hover:bg-muted w-full transition-colors ${expanded ? "px-3" : "justify-center px-0"}`}>
              <HelpCircle className="h-4 w-4 shrink-0" /> {expanded && "Help Centre"}
            </a>
            <button onClick={() => setShowSwitch(true)} data-testid="switch-user-button" title={!expanded ? "Switch user" : undefined}
              className={`flex items-center gap-2 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-primary hover:bg-muted w-full transition-colors ${expanded ? "px-3" : "justify-center px-0"}`}>
              <Repeat className="h-4 w-4 shrink-0" /> {expanded && "Switch user"}
            </button>
            <button onClick={() => { logout(); navigate("/login"); }} data-testid="logout-button" title={!expanded ? "Sign out" : undefined}
              className={`flex items-center gap-2 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-destructive hover:bg-muted w-full transition-colors ${expanded ? "px-3" : "justify-center px-0"}`}>
              <LogOut className="h-4 w-4 shrink-0" /> {expanded && "Sign out"}
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="xl:hidden fixed inset-0 z-50 flex">
          <div className="w-64 bg-card h-full p-3 overflow-y-auto shadow-xl">
            <div className="flex items-center justify-between mb-4 px-2">
              <span className="font-heading font-bold">{business?.name}</span>
              <button onClick={() => setOpen(false)}><X className="h-5 w-5" /></button>
            </div>
            {NavItems({ mobile: true })}
            <a href={APP_STORE_URL} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-3 py-2 text-sm text-primary" data-testid="mapp-download-link">
              <Smartphone className="h-4 w-4" /> Get the iPhone app
            </a>
            <a href="/help" target="_blank" rel="noreferrer" className="flex items-center gap-2 px-3 py-2 text-sm text-primary" data-testid="mhelp-link">
              <HelpCircle className="h-4 w-4" /> Help Centre
            </a>
            <button onClick={() => { setOpen(false); setShowSwitch(true); }} className="flex items-center gap-2 px-3 py-2 text-sm text-primary" data-testid="mswitch-user-button">
              <Repeat className="h-4 w-4" /> Switch user
            </button>
            <button onClick={() => { logout(); navigate("/login"); }} className="flex items-center gap-2 px-3 py-2 text-sm text-destructive" data-testid="mlogout-button">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
          <div className="flex-1 bg-black/50" onClick={() => setOpen(false)} />
        </div>
      )}

      {/* Content */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className={`${isCalendar ? "hidden" : "xl:hidden"} h-12 flex items-center gap-3 px-4 border-b border-border sticky top-0 bg-background/90 backdrop-blur z-40`}>
          {business?.name ? <span className="font-heading font-bold text-sm">{business.name}</span> : <img src="/azordi-logo.webp" alt="Azordi" className="h-5 w-auto" />}
        </header>
        <main className="flex-1 min-w-0 overflow-x-clip pb-14 xl:pb-0"><TrialBanner /><LowCreditBanner /><Outlet /></main>
      </div>

      {/* Mobile bottom nav */}
      {(() => {
        const rk = roleKey(user?.role);
        const PRIMARY = [
          { to: "/calendar", label: "Calendar", icon: CalendarDays },
          { to: "/dashboard", label: "Home", icon: LayoutDashboard, end: true, roles: ["admin", "staff"], perm: "view_dashboard" },
          { to: "/clients", label: "Clients", icon: Users, roles: ["admin", "reception", "staff"] },
          { to: "/messages", label: "Messages", icon: MessageSquare, roles: ["admin"] },
        ].filter((it) => !it.roles || it.roles.includes(rk) || (it.perm && can(it.perm))).slice(0, rk === "admin" ? 3 : 4);
        return (
          <nav className="xl:hidden fixed bottom-0 left-0 right-0 z-40 h-14 bg-card/95 backdrop-blur border-t border-border flex items-stretch pb-[env(safe-area-inset-bottom)]" data-testid="mobile-bottom-nav">
            {PRIMARY.map((it) => (
              <NavLink key={it.to} to={it.to} end={it.end} data-testid={`bnav-${it.label.toLowerCase()}`}
                className={({ isActive }) => `flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors active:scale-95 ${isActive ? "text-primary" : "text-muted-foreground"}`}>
                <it.icon className="h-[18px] w-[18px]" />
                {it.label}
              </NavLink>
            ))}
            {rk === "admin" && <NotificationBell inline />}
            <button onClick={() => setOpen(true)} data-testid="bnav-more"
              className="flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium text-muted-foreground active:scale-95">
              <Menu className="h-[18px] w-[18px]" />
              More
            </button>
          </nav>
        );
      })()}
      <NotificationBell />
      {setupOpen && <SetupMenu />}
      {showSwitch && <UserSwitchOverlay onClose={() => setShowSwitch(false)} onDone={() => setShowSwitch(false)} />}
      {locked && lockMins !== 0 && <UserSwitchOverlay lockMode onDone={unlock} />}
    </div>
  );
};
